import { useState } from 'react';
import {
  AutocompleteInput, Button, Form, LinkBase, ReferenceInput, useCloseNotification, useCreatePath, useNotify, useRefresh,
} from 'react-admin';
import {
  Alert, Box, Button as MuiButton, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { SendOfferError, SendOfferResult } from '../../models/attendantGoalOffer';
import { sendOffer } from '../../services/attendantGoalOfferApi';
import { filterToQuery } from '../../utils';
import { SendRefusal, sendRefusal, sentMessage } from './labels';

interface ChainOption { id: string; name: string }

/** Aviso de sucesso com atalho para o convite: o texto já vem traduzido e o link fecha o aviso. */
const SentNotice = ({ message, offerId }: { message: string; offerId: string }) => {
  const close = useCloseNotification();
  const createPath = useCreatePath();
  return (
    <Alert
      severity="success"
      variant="filled"
      action={(
        <MuiButton
          color="inherit"
          size="small"
          component={LinkBase}
          to={createPath({ resource: 'attendant-goal-offers', type: 'show', id: offerId })}
          onClick={close}
        >
          Ver convite
        </MuiButton>
      )}
    >
      {message}
    </Alert>
  );
};

const SendOfferDialog = ({ onClose, onSent }: { onClose: () => void; onSent: () => void }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [chain, setChain] = useState<ChainOption | null>(null);
  const [sending, setSending] = useState(false);
  const [refusal, setRefusal] = useState<(SendRefusal & { severity: 'warning' | 'error' }) | null>(null);

  // A análise é síncrona (5 a 30 s): `sending` trava o envio duplo e o fechamento até a resposta chegar.
  const send = async () => {
    if (!chain || sending) { return; }
    setSending(true);
    setRefusal(null);
    let result: SendOfferResult;
    try {
      result = await sendOffer(chain.id);
    } catch (err) {
      const error = err as SendOfferError;
      // 409 é regra de negócio (aviso); 404, 503 e falha de rede são erro.
      setRefusal({ ...sendRefusal(error), severity: error.status === 409 ? 'warning' : 'error' });
      setSending(false);
      return;
    }
    notify(<SentNotice message={sentMessage(result, chain.name)} offerId={result.offerId} />, { type: 'success', autoHideDuration: 10000 });
    refresh();
    onSent();
    onClose();
  };

  return (
    <Dialog open onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="send-offer-title">
      <DialogTitle id="send-offer-title">Enviar convite</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          O dono da rede vê o convite no painel (card na Home, pop-up e sininho). A degustação de 60 dias só começa quando
          ele clicar em Ativar. Antes de enviar, a rede é analisada na hora: sem gatilho, nada é enviado.
        </Typography>
        {/* O <Form> só dá contexto ao campo. Enter não envia: o envio é um clique em "Enviar", com a rede escolhida à vista. */}
        <Form onSubmit={() => undefined}>
          <ReferenceInput source="chainId" reference="chains" sort={{ field: 'name', order: 'ASC' }} perPage={25}>
            <AutocompleteInput
              label="Rede"
              optionText="name"
              filterToQuery={filterToQuery}
              disabled={sending}
              onChange={(_, record) => { setChain(record ? { id: String(record.id), name: record.name } : null); setRefusal(null); }}
              sx={{ width: 420, maxWidth: '100%' }}
            />
          </ReferenceInput>
        </Form>
        {refusal && (
          <Alert severity={refusal.severity} sx={{ mt: 1 }}>
            {refusal.message}
            {!!refusal.details?.length && (
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {refusal.details.map(line => <li key={line}>{line}</li>)}
              </Box>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose} disabled={sending}>Cancelar</MuiButton>
        <MuiButton
          variant="contained"
          onClick={send}
          disabled={!chain || sending}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {sending ? 'Analisando a rede…' : 'Enviar'}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

/** Botão da barra da lista de convites: abre o diálogo de envio manual (só aparece dentro do InternalGate). */
const SendOfferButton = ({ onSent }: { onSent: () => void }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button label="Enviar convite" onClick={() => setOpen(true)}><SendIcon /></Button>
      {open && <SendOfferDialog onClose={() => setOpen(false)} onSent={onSent} />}
    </>
  );
};

export default SendOfferButton;
